import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateProjectSchema, type CreateProjectInput } from '@cutlyft/zod-schemas';
import { useCreateProject } from '@/hooks/projects/use-create-project';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';

interface CreateProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (projectId: number) => void;
}

/**
 * Modal for creating a new fabrication project.
 * Validates via CreateProjectSchema; only name is required.
 */
export function CreateProjectModal({ open, onOpenChange, onCreated }: CreateProjectModalProps) {
  const { toast } = useToast();
  const createProjectMutation = useCreateProject();

  const form = useForm<CreateProjectInput>({
    resolver: zodResolver(CreateProjectSchema),
    defaultValues: {
      name: '',
      reference: '',
      clientName: '',
      clientContact: '',
      remarks: '',
    },
  });

  const onSubmit = (values: CreateProjectInput) => {
    const payload: CreateProjectInput = {
      name: values.name,
      ...(values.reference?.trim() && { reference: values.reference.trim() }),
      ...(values.clientName?.trim() && { clientName: values.clientName.trim() }),
      ...(values.clientContact?.trim() && { clientContact: values.clientContact.trim() }),
      ...(values.remarks?.trim() && { remarks: values.remarks.trim() }),
    };

    createProjectMutation.mutate(payload, {
      onSuccess: (data) => {
        toast({ title: 'Project created', description: `"${data.name}" is ready.` });
        form.reset();
        onOpenChange(false);
        onCreated?.(data.id);
      },
      onError: (err: unknown) => {
        const apiErr = err as { response?: { data?: { error?: { message?: string } } } };
        const msg = apiErr?.response?.data?.error?.message ?? '';
        if (msg.includes('PLAN_LIMIT_EXCEEDED')) {
          toast({
            variant: 'destructive',
            title: 'Plan limit reached',
            description:
              'You have reached the maximum number of projects for your plan. Upgrade to create more.',
          });
        } else {
          toast({
            variant: 'destructive',
            title: 'Failed to create project',
            description: msg || 'An unexpected error occurred.',
          });
        }
      },
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) form.reset();
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>New Project</DialogTitle>
          <DialogDescription>
            Create a new fabrication project. Only the project name is required.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Project Name <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. 25 Greenfield Drive — Renovation" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reference"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reference / Job Number</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. JOB-2026-001" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="clientName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Sunrise Builders Ltd." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="clientContact"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client Contact</FormLabel>
                  <FormControl>
                    <Input placeholder="Email, phone, or address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="remarks"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Remarks</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional notes" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createProjectMutation.isPending}>
                {createProjectMutation.isPending ? 'Creating…' : 'Create Project'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
